import React, { useState, useEffect, ChangeEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/lib/customSupabaseClient';
import { useAuth } from '@/contexts/SupabaseAuthContext';
import { useToast } from '@/components/ui/use-toast';
import { Loader2, Upload, Check, X, RotateCw } from 'lucide-react';
import { motion } from 'framer-motion';
import { Profile } from '@/types/db';
import { CREDITS_VISIBLE } from '@/config/stripe';
import ProfilePurchases from '@/components/ProfilePurchases';

interface UsageOption { id: string; label: string; description: string; items: { id: string; label: string }[]; }
interface AmenityOption { id: string; label: string; }
interface ProductOption { id: string; label: string; }
interface ProfileFormState {
  first_name: string; last_name: string; bio: string; birthday_month: string; birthday_day: string; phone: string;
  preferred_name: string; emergency_name: string; emergency_phone: string;
  room_temperature: string; room_sound: string; room_scent: string; access_needs: string[];
  usage_items: Record<string, string[]>;
  photo_url?: string | null; member_number?: number | null; credits?: number | null;
  preferences_amenities: string[]; preferences_usage: string[]; preferences_products: string[];
}
type PreferenceCategory = 'preferences_amenities' | 'preferences_usage' | 'preferences_products' | 'access_needs';

const MONTHS = [
  { value: '01', label: 'January' }, { value: '02', label: 'February' }, { value: '03', label: 'March' },
  { value: '04', label: 'April' }, { value: '05', label: 'May' }, { value: '06', label: 'June' },
  { value: '07', label: 'July' }, { value: '08', label: 'August' }, { value: '09', label: 'September' },
  { value: '10', label: 'October' }, { value: '11', label: 'November' }, { value: '12', label: 'December' },
];

const getDaysInMonth = (month: string): number => {
  if (!month) return 31;
  const m = parseInt(month, 10);
  if ([4, 6, 9, 11].includes(m)) return 30;
  if (m === 2) return 29; // Allow Feb 29 for leap year babies
  return 31;
};

const getOrdinalSuffix = (day: number): string => {
  if (day >= 11 && day <= 13) return 'th';
  switch (day % 10) {
    case 1: return 'st';
    case 2: return 'nd';
    case 3: return 'rd';
    default: return 'th';
  }
};

const formatBirthdayDisplay = (month: string, day: string): string => {
  if (!month || !day) return '';
  const monthObj = MONTHS.find(m => m.value === month);
  const dayNum = parseInt(day, 10);
  if (!monthObj || isNaN(dayNum)) return '';
  return `${monthObj.label} ${dayNum}${getOrdinalSuffix(dayNum)}`;
};

const parseDob = (dob: string | null): { month: string; day: string } => {
  if (!dob) return { month: '', day: '' };
  // Handle both YYYY-MM-DD (legacy) and MM-DD formats
  const parts = dob.split('-');
  if (parts.length === 3) {
    // YYYY-MM-DD format - strip year
    return { month: parts[1], day: parts[2] };
  } else if (parts.length === 2) {
    // MM-DD format
    return { month: parts[0], day: parts[1] };
  }
  return { month: '', day: '' };
};

const formatDobForStorage = (month: string, day: string): string => {
  if (!month || !day) return '';
  // dob column is DATE. Year 2000 is a sentinel (leap year, permits Feb 29) —
  // we only collect month/day; parseDob strips the year on load.
  return `2000-${month}-${day.padStart(2, '0')}`;
};

const usageOptions: UsageOption[] = [
  {
    id: 'prayer', label: 'Prayer & Meditation',
    description: 'A discreet qibla marker is built into every room and the Hull — no need to ask.',
    items: [
      { id: 'rug', label: 'Prayer rug' },
      { id: 'cushion', label: 'Meditation cushion' },
    ],
  },
  {
    id: 'pumping', label: 'Nursing & Pumping',
    description: 'A nursing pump is available in-room. Pump sessions book as a longer visit — we set up before you arrive and sanitize everything after.',
    items: [
      { id: 'pump', label: 'Nursing pump' },
      { id: 'milk_bags', label: 'Milk storage bags' },
      { id: 'nipple_cream', label: 'Nipple cream' },
      { id: 'wipes', label: 'Wipes' },
      { id: 'nursing_pillow', label: 'Nursing pillow' },
    ],
  },
  {
    id: 'change', label: 'Outfit Change & Interview Prep',
    description: 'Walk out pressed and put together.',
    items: [
      { id: 'mirror', label: 'Full-length mirror' },
      { id: 'steamer', label: 'Iron & steamer' },
      { id: 'lint_roller', label: 'Lint roller' },
      { id: 'privacy_screen', label: 'Privacy screen' },
    ],
  },
  {
    id: 'decompress', label: 'Sensory Decompression',
    description: 'The room, turned all the way down.',
    items: [
      { id: 'headphones', label: 'Noise-canceling headphones' },
      { id: 'blanket', label: 'Weighted blanket' },
      { id: 'dim', label: 'Dim lighting' },
      { id: 'white_noise', label: 'White noise' },
    ],
  },
];
const amenityOptions: AmenityOption[] = [
  { id: 'heated_seat', label: 'Heated Seat' },
  { id: 'dim_lights', label: 'Dimmed Lighting' },
  { id: 'white_noise', label: 'White Noise' },
  { id: 'hair_dryer', label: 'Hair Dryer' },
  { id: 'steamer', label: 'Garment Steamer' },
];
const productOptions: ProductOption[] = [
  { id: 'ursa_major', label: 'Ursa Major Face Wash' },
  { id: 'spf', label: 'SPF Lotion' },
  { id: 'mouthwash', label: 'Mouthwash' },
  { id: 'feminine_care', label: 'Organic Feminine Care' },
];

const TAB_NAMES = ['About You', 'Room Settings', 'Kits', 'Samples'];

const ProfilePage: React.FC = () => {
  const { user } = useAuth();
  const { toast } = useToast();
  const navigate = useNavigate();
  const [loading, setLoading] = useState<boolean>(true);
  const [saving, setSaving] = useState<boolean>(false);
  const [saved, setSaved] = useState<boolean>(false);
  const [dirty, setDirty] = useState<boolean>(false);
  const [tab, setTab] = useState<number>(0);
  // "Your place" (presale purchases) is a fifth tab that only exists when the account
  // has at least one non-expired purchase. Cheap head-count query; the tab body loads
  // the rows itself. Patrick, 9/12: the full card above the rail was too much.
  const [purchaseCount, setPurchaseCount] = useState<number>(0);
  const userId = user?.id ?? null;
  useEffect(() => {
    if (!userId) return;
    let cancelled = false;
    supabase
      .from('purchases')
      .select('id', { count: 'exact', head: true })
      .eq('user_id', userId)
      .neq('status', 'expired')
      .then(({ count }) => {
        if (!cancelled) setPurchaseCount(count ?? 0);
      });
    return () => {
      cancelled = true;
    };
  }, [userId]);
  const tabNames = purchaseCount > 0 ? [...TAB_NAMES, 'Your place'] : TAB_NAMES;
  const [avatarUrl, setAvatarUrl] = useState<string | null>(null);
  const [isZoomed, setIsZoomed] = useState<boolean>(false);
  // Full loaded preferences jsonb, retained so save can merge against it and
  // preserve keys the UI doesn't manage (suite / samples / provisions).
  const [preferencesRaw, setPreferencesRaw] = useState<Record<string, unknown>>({});
  const [profile, setProfile] = useState<ProfileFormState>({
    first_name: '', last_name: '', bio: '', birthday_month: '', birthday_day: '', phone: '',
    preferred_name: '', emergency_name: '', emergency_phone: '',
    room_temperature: '', room_sound: '', room_scent: '', access_needs: [], usage_items: {},
    preferences_amenities: [], preferences_usage: [], preferences_products: []
  });

  // Every user edit goes through this so the save bar knows the page is dirty.
  const edit = (patch: Partial<ProfileFormState>) => {
    setProfile(prev => ({ ...prev, ...patch }));
    setDirty(true);
    setSaved(false);
  };

  useEffect(() => {
    const getProfile = async () => {
      try {
        if (!user) return;
        const { data, error } = await ((supabase as any).schema('v2').from('profiles') as any).select('*').eq('id', user.id).single();
        if (error && error.code !== 'PGRST116') throw error;
        if (data) {
          const p = data as Profile;
          const { month, day } = parseDob(p.dob);
          // The three toggle groups live inside the preferences jsonb. Retain the
          // whole object so save can merge and keep suite / samples / provisions.
          const prefs = ((p as any).preferences as Record<string, unknown> | null) || {};
          setPreferencesRaw(prefs);
          const loadedUsage = (prefs.usage as string[]) || [];
          const rawKitItems = (prefs.usage_items as Record<string, string[]>) || {};
          // Kits selected before item-level choice existed default to "everything".
          const loadedUsageItems = Object.fromEntries(
            loadedUsage.map(k => [k, rawKitItems[k] ?? (usageOptions.find(o => o.id === k)?.items.map(i => i.id) || [])])
          );
          setProfile({
            first_name: p.first_name || '', last_name: p.last_name || '',
            bio: p.bio || '', birthday_month: month, birthday_day: day, phone: p.phone || '',
            photo_url: p.photo_url || null, member_number: p.member_number || null,
            credits: (p as any).credit_balance || 0,
            preferences_amenities: (prefs.amenities as string[]) || [],
            preferences_usage: loadedUsage,
            usage_items: loadedUsageItems,
            preferences_products: (prefs.products as string[]) || [],
            preferred_name: (prefs.preferred_name as string) || '',
            emergency_name: ((prefs.emergency_contact as { name?: string; phone?: string } | null)?.name) || '',
            emergency_phone: ((prefs.emergency_contact as { name?: string; phone?: string } | null)?.phone) || '',
            room_temperature: ((prefs.room as { temperature?: string; sound?: string; scent?: string } | null)?.temperature) || '',
            room_sound: ((prefs.room as { temperature?: string; sound?: string; scent?: string } | null)?.sound) || '',
            room_scent: ((prefs.room as { temperature?: string; sound?: string; scent?: string } | null)?.scent) || '',
            access_needs: (prefs.access as string[]) || [],
          });
          if (p.photo_url) downloadImage(p.photo_url);
        }
      } catch (e) { console.error(e); } finally { setLoading(false); }
    };
    getProfile();
  }, [user]);

  const downloadImage = async (path: string) => {
    try {
      const { data } = supabase.storage.from('avatars').getPublicUrl(path);
      setAvatarUrl(`${data.publicUrl}?t=${Date.now()}`);
    } catch (e) { setAvatarUrl(null); }
  };

  const uploadAvatar = async (event: ChangeEvent<HTMLInputElement>) => {
    try {
      setSaving(true);
      if (!event.target.files?.length || !user) return;
      const file = event.target.files[0];
      const filePath = `${user.id}/${Date.now()}.${file.name.split('.').pop()}`;
      if (profile.photo_url) {
        try { await supabase.storage.from('avatars').remove([profile.photo_url]); } catch {}
      }
      const { error: uploadError } = await supabase.storage.from('avatars').upload(filePath, file, { upsert: false });
      if (uploadError) throw uploadError;
      const { data: existing } = await ((supabase as any).schema('v2').from('profiles') as any).select('*').eq('id', user.id).maybeSingle();
      const now = new Date().toISOString();
      if (!existing) {
        await ((supabase as any).schema('v2').from('profiles') as any).insert({ id: user.id, photo_url: filePath, updated_at: now });
      } else {
        await ((supabase as any).schema('v2').from('profiles') as any).update({ photo_url: filePath, updated_at: now }).eq('id', user.id);
      }
      setProfile(prev => ({ ...prev, photo_url: filePath }));
      const { data } = supabase.storage.from('avatars').getPublicUrl(filePath);
      setAvatarUrl(`${data.publicUrl}?t=${Date.now()}`);
      toast({ title: "Photo updated.", className: "bg-maslow-navy text-maslow-cream border-[color-mix(in_srgb,var(--gold)_20%,transparent)]" });
    } catch (e) {
      toast({ title: "Upload failed.", description: (e as Error).message, variant: "destructive" });
    } finally { setSaving(false); }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      if (!user) return;
      const dob = formatDobForStorage(profile.birthday_month, profile.birthday_day);
      // Only existing, user-editable columns. Deliberately excluded: credits/
      // credit_balance and member_number (server-owned, display-only) and email
      // (would overwrite the phone account's stored email).
      // The three toggle groups are merged into the preferences jsonb, preserving
      // the keys the UI doesn't manage (suite / samples / provisions).
      const preferences = {
        ...preferencesRaw,
        amenities: profile.preferences_amenities,
        usage: profile.preferences_usage,
        products: profile.preferences_products,
        preferred_name: profile.preferred_name || null,
        emergency_contact: (profile.emergency_name || profile.emergency_phone)
          ? { name: profile.emergency_name, phone: profile.emergency_phone }
          : null,
        room: {
          temperature: profile.room_temperature || null,
          sound: profile.room_sound || null,
          scent: profile.room_scent || null,
        },
        access: profile.access_needs,
        usage_items: profile.usage_items,
      };
      const updates = {
        first_name: profile.first_name,
        last_name: profile.last_name,
        bio: profile.bio,
        dob: dob || null,
        phone: profile.phone,
        preferences,
        updated_at: new Date().toISOString(),
      };
      const { error } = await ((supabase as any).schema('v2').from('profiles') as any).upsert({ id: user.id, ...updates });
      if (error) throw error;
      setDirty(false);
      setSaved(true);
      setTimeout(() => setSaved(false), 2500);
    } catch (e) {
      toast({ title: "Error saving.", description: (e as Error).message, variant: "destructive" });
    } finally { setSaving(false); }
  };

  const togglePreference = (category: PreferenceCategory, itemId: string) => {
    setProfile(prev => {
      const list = prev[category] || [];
      return { ...prev, [category]: list.includes(itemId) ? list.filter(id => id !== itemId) : [...list, itemId] };
    });
    setDirty(true);
    setSaved(false);
  };

  // Selecting a kit defaults to everything in it; members then uncheck what they don't need.
  const toggleKit = (kitId: string) => {
    setProfile(prev => {
      const selected = prev.preferences_usage.includes(kitId);
      const usage = selected ? prev.preferences_usage.filter(id => id !== kitId) : [...prev.preferences_usage, kitId];
      const usage_items = { ...prev.usage_items };
      if (selected) delete usage_items[kitId];
      else usage_items[kitId] = usageOptions.find(o => o.id === kitId)?.items.map(i => i.id) || [];
      return { ...prev, preferences_usage: usage, usage_items };
    });
    setDirty(true);
    setSaved(false);
  };

  const toggleKitItem = (kitId: string, itemId: string) => {
    setProfile(prev => {
      const list = prev.usage_items[kitId] || [];
      const next = list.includes(itemId) ? list.filter(id => id !== itemId) : [...list, itemId];
      return { ...prev, usage_items: { ...prev.usage_items, [kitId]: next } };
    });
    setDirty(true);
    setSaved(false);
  };

  const memberSince = new Date(user?.created_at || Date.now()).toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
  const monogram = `${(profile.first_name || 'M').charAt(0)}${(profile.last_name || '').charAt(0)}`.toUpperCase();

  if (loading) return (
    <div className="min-h-screen flex items-center justify-center bg-maslow-cream">
      <Loader2 className="animate-spin w-6 h-6 text-[color-mix(in_srgb,var(--navy)_40%,transparent)]" />
    </div>
  );

  return (
    <div className="min-h-screen bg-maslow-cream pb-28" style={{ fontFamily: 'var(--sans)' }}>

      {/* ── HEADER BAND ── */}
      <div className="bg-maslow-navy px-6 py-8 md:px-12">
        <div className="max-w-3xl mx-auto flex items-center gap-6 md:gap-8">

          {/* Avatar / monogram */}
          <div className="relative group flex-shrink-0">
            <div
              className="w-20 h-20 md:w-24 md:h-24 rounded-full overflow-hidden border border-[color-mix(in_srgb,var(--gold)_55%,transparent)] cursor-pointer transition-colors hover:border-maslow-gold"
              onClick={() => avatarUrl && setIsZoomed(true)}
            >
              {avatarUrl
                ? <img src={avatarUrl} alt="Avatar" className="w-full h-full object-cover" />
                : (
                  <div className="w-full h-full flex items-center justify-center">
                    <span className="text-maslow-gold text-2xl md:text-3xl tracking-wide" style={{ fontFamily: 'var(--serif)' }}>{monogram}</span>
                  </div>
                )
              }
            </div>
            <label className="absolute -bottom-0.5 -right-0.5 bg-maslow-navy border border-[color-mix(in_srgb,var(--gold)_55%,transparent)] text-maslow-gold p-1.5 rounded-full cursor-pointer hover:border-maslow-gold transition-colors" htmlFor="avatar-upload">
              <Upload className="w-3 h-3" />
            </label>
            <input type="file" id="avatar-upload" accept="image/*" onChange={uploadAvatar} className="hidden" disabled={saving} />
          </div>

          {/* Name + metadata */}
          <div className="flex-1 min-w-0">
            <h1 className="text-2xl md:text-4xl text-maslow-cream leading-tight truncate" style={{ fontFamily: 'var(--serif)', fontWeight: 400 }}>
              {profile.first_name ? `${profile.first_name}${profile.last_name ? ` ${profile.last_name}` : ''}` : 'Welcome'}
            </h1>
            <p className="text-[color-mix(in_srgb,var(--cream)_65%,transparent)] text-sm tracking-wide mt-1.5">
              Member since {memberSince} &nbsp;·&nbsp; No. {(profile.member_number || 0).toLocaleString()}
            </p>
          </div>

          {/* Credits — hidden until the pricing gate opens (CREDITS_VISIBLE) */}
          {CREDITS_VISIBLE && (
            <div className="text-right flex-shrink-0">
              <p className="text-[color-mix(in_srgb,var(--gold)_65%,transparent)] uppercase tracking-[0.22em] text-[10px] mb-1">Credits</p>
              <p className="text-maslow-cream text-3xl leading-none" style={{ fontFamily: 'var(--serif)' }}>{profile.credits || 0}</p>
              <a href="/buy-credits" className="text-[color-mix(in_srgb,var(--gold)_70%,transparent)] hover:text-maslow-gold text-[11px] tracking-wide mt-1 inline-block border-b border-[color-mix(in_srgb,var(--gold)_35%,transparent)] hover:border-maslow-gold transition-colors">
                Add credits
              </a>
            </div>
          )}
        </div>
      </div>

      {/* ── BODY: tab rail + one surface per tab ── */}
      <div className="max-w-3xl mx-auto px-6 md:px-0 pt-6">

        {/* Tab rail — same editorial rail as /rooms and /hull */}
        <div className="flex flex-wrap gap-x-8 gap-y-1 px-2 mb-4" role="tablist" aria-label="Profile sections">
          {tabNames.map((name, i) => (
            <button
              key={name}
              role="tab"
              aria-selected={tab === i}
              onClick={() => setTab(i)}
              className={`relative py-2 text-[11px] uppercase tracking-[0.26em] transition-opacity duration-300 ${tab === i ? 'text-maslow-navy opacity-100' : 'text-maslow-navy opacity-70 hover:opacity-100'}`}
            >
              {name}
              <span className={`absolute left-0 right-0 bottom-0 h-px bg-maslow-gold origin-left transition-transform duration-300 ${tab === i ? 'scale-x-100' : 'scale-x-0'}`} />
            </button>
          ))}
        </div>

        <motion.div
          key={tab}
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.35 }}
          className="bg-[color-mix(in_srgb,#ffffff_50%,var(--cream))] border border-[rgba(212,175,106,0.3)] rounded-sm shadow-[0_24px_60px_-40px_rgba(28,43,58,0.45)] p-5 md:p-8"
        >

        {/* TAB 0 — ABOUT YOU */}
        {tab === 0 && (
        <section>
          <SectionLabel>Identity</SectionLabel>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-10 gap-y-5 mt-5">
            <Field label="First Name">
              <LineInput value={profile.first_name} onChange={v => edit({ first_name: v })} placeholder="First" />
            </Field>
            <Field label="Last Name">
              <LineInput value={profile.last_name} onChange={v => edit({ last_name: v })} placeholder="Last" />
            </Field>
            <Field label="What Should We Call You">
              <LineInput value={profile.preferred_name} onChange={v => edit({ preferred_name: v })} placeholder="e.g. Pat" />
            </Field>
            <Field label="Phone">
              <LineInput value={profile.phone} onChange={v => edit({ phone: v })} placeholder="(212) 555-0100" type="tel" />
            </Field>
            <Field label="Birthday">
              <div className="flex gap-6 items-end">
                <select
                  value={profile.birthday_month}
                  onChange={e => {
                    const newMonth = e.target.value;
                    const maxDay = getDaysInMonth(newMonth);
                    const currentDay = parseInt(profile.birthday_day, 10);
                    edit({
                      birthday_month: newMonth,
                      birthday_day: currentDay > maxDay ? String(maxDay).padStart(2, '0') : profile.birthday_day,
                    });
                  }}
                  className="flex-1 bg-transparent text-base text-maslow-navy border-0 border-b border-[color-mix(in_srgb,var(--navy)_25%,transparent)] focus:border-maslow-gold rounded-none px-0 py-2 outline-none appearance-none cursor-pointer transition-colors"
                >
                  <option value="">Month</option>
                  {MONTHS.map(m => <option key={m.value} value={m.value}>{m.label}</option>)}
                </select>
                <select
                  value={profile.birthday_day}
                  onChange={e => edit({ birthday_day: e.target.value })}
                  className="w-16 bg-transparent text-base text-maslow-navy border-0 border-b border-[color-mix(in_srgb,var(--navy)_25%,transparent)] focus:border-maslow-gold rounded-none px-0 py-2 outline-none appearance-none cursor-pointer transition-colors"
                >
                  <option value="">Day</option>
                  {Array.from({ length: getDaysInMonth(profile.birthday_month) }, (_, i) => {
                    const day = String(i + 1).padStart(2, '0');
                    return <option key={day} value={day}>{i + 1}</option>;
                  })}
                </select>
              </div>
              {profile.birthday_month && profile.birthday_day && (
                <p className="text-xs text-maslow-gold mt-2 italic" style={{ fontFamily: 'var(--serif)' }}>
                  {formatBirthdayDisplay(profile.birthday_month, profile.birthday_day)}
                </p>
              )}
            </Field>
            <Field label="Notes for Staff">
              <textarea
                value={profile.bio}
                onChange={e => edit({ bio: e.target.value })}
                placeholder="e.g. 'I prefer a quiet greeting'"
                rows={2}
                className="w-full bg-transparent text-base text-maslow-navy border-0 border-b border-[color-mix(in_srgb,var(--navy)_25%,transparent)] focus:border-maslow-gold rounded-none px-0 py-2 resize-none outline-none placeholder-[color-mix(in_srgb,var(--navy)_35%,transparent)] transition-colors"
              />
            </Field>
            <Field label="Emergency Contact">
              <LineInput value={profile.emergency_name} onChange={v => edit({ emergency_name: v })} placeholder="Optional — for wellness checks" />
            </Field>
            <Field label="Emergency Contact Phone">
              <LineInput value={profile.emergency_phone} onChange={v => edit({ emergency_phone: v })} placeholder="Their number" type="tel" />
            </Field>
          </div>
        </section>
        )}

        {/* TAB 1 — ROOM SETTINGS */}
        {tab === 1 && (
        <section>
          <SectionLabel sub="How should we prep the room?">The Usual</SectionLabel>
          <div className="mt-3">
            <ChoiceRow
              label="Temperature"
              value={profile.room_temperature}
              onChange={v => edit({ room_temperature: v })}
              options={[{ id: 'warm', label: 'Warm' }, { id: 'neutral', label: 'Neutral' }, { id: 'cool', label: 'Cool' }]}
            />
            <ChoiceRow
              label="Sound on arrival"
              value={profile.room_sound}
              onChange={v => edit({ room_sound: v })}
              options={[{ id: 'ambient', label: 'Calm ambient' }, { id: 'silence', label: 'Silence' }, { id: 'own', label: "I'll play my own" }]}
            />
            <ChoiceRow
              label="Scent"
              description="The house scent is a Japanese cedar plank at the air vent — wood, not spray"
              value={profile.room_scent}
              onChange={v => edit({ room_scent: v })}
              options={[{ id: 'cedar', label: 'Cedar' }, { id: 'unscented', label: 'Unscented' }]}
            />
            {amenityOptions.map(item => (
              <PickRow
                key={item.id}
                label={item.label}
                checked={(profile.preferences_amenities || []).includes(item.id)}
                onChange={() => togglePreference('preferences_amenities', item.id)}
              />
            ))}
            <PickRow
              label="Adult changing table"
              description="A padded table top we clamp secure over the bench before you arrive"
              checked={(profile.access_needs || []).includes('adult_changing_table')}
              onChange={() => togglePreference('access_needs', 'adult_changing_table')}
            />
          </div>
        </section>
        )}

        {/* TAB 2 — KITS */}
        {tab === 2 && (
        <section>
          <SectionLabel sub="Pick a kit, then choose exactly what's set out">Usage Kits</SectionLabel>
          <div className="mt-3">
            {usageOptions.map(option => (
              <KitRow
                key={option.id}
                option={option}
                checked={(profile.preferences_usage || []).includes(option.id)}
                items={profile.usage_items[option.id] || []}
                onToggle={() => toggleKit(option.id)}
                onToggleItem={itemId => toggleKitItem(option.id, itemId)}
              />
            ))}
          </div>
        </section>
        )}

        {/* TAB 4 — YOUR PLACE (presale purchases). The tab exists only when the account has one. */}
        {tab === 4 && user && (
          <ProfilePurchases userId={user.id} />
        )}

        {/* TAB 3 — SAMPLES */}
        {tab === 3 && (
        <section>
          <SectionLabel sub="What should we stock for you?">Ethical Toiletries</SectionLabel>
          <div className="mt-3">
            {productOptions.map(item => (
              <PickRow
                key={item.id}
                label={item.label}
                checked={(profile.preferences_products || []).includes(item.id)}
                onChange={() => togglePreference('preferences_products', item.id)}
              />
            ))}
          </div>
        </section>
        )}

        </motion.div>

        {/* Settings + note */}
        <section className="pt-8 pb-2">
          <button
            onClick={() => navigate('/profile/settings')}
            className="group flex items-baseline justify-between w-full text-left"
          >
            <span className="text-base text-maslow-navy tracking-wide group-hover:text-maslow-gold transition-colors">Account settings</span>
            <span className="text-sm text-[color-mix(in_srgb,var(--navy)_55%,transparent)]">Security, notifications &amp; more &rarr;</span>
          </button>
          <p className="mt-8 text-[15px] italic text-[color-mix(in_srgb,var(--navy)_55%,transparent)] border-l-2 border-[color-mix(in_srgb,var(--gold)_60%,transparent)] pl-4" style={{ fontFamily: 'var(--serif)' }}>
            Every preference you set is a note passed to the room before you arrive.
          </p>
        </section>
      </div>

      {/* ── SAVE BAR: appears only when something changed ── */}
      {(dirty || saved) && (
        <div className="fixed bottom-0 inset-x-0 z-40 bg-[color-mix(in_srgb,var(--cream)_92%,transparent)] backdrop-blur-md border-t border-[color-mix(in_srgb,var(--navy)_10%,transparent)]">
          <div className="max-w-3xl mx-auto px-6 md:px-0 py-3.5 flex items-center justify-between">
            <span className="text-sm text-[color-mix(in_srgb,var(--navy)_60%,transparent)] tracking-wide">
              {saved ? 'Saved.' : 'Unsaved changes'}
            </span>
            <button
              onClick={handleSave}
              disabled={saving || !dirty}
              className={`text-xs uppercase tracking-[0.22em] px-6 py-2.5 transition-colors flex items-center gap-2 ${
                saved
                  ? 'text-maslow-navy'
                  : 'bg-maslow-navy text-maslow-cream hover:bg-maslow-gold hover:text-maslow-navy'
              }`}
            >
              {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : saved ? <Check className="w-3 h-3" /> : null}
              {saved ? 'Saved' : 'Save changes'}
            </button>
          </div>
        </div>
      )}

      {/* ── ZOOMED AVATAR ── */}
      {isZoomed && (
        <div className="fixed inset-0 z-[100] bg-black/90 flex flex-col items-center justify-center p-4" onClick={() => setIsZoomed(false)}>
          <button className="absolute top-4 right-4 text-white/60 hover:text-white" onClick={() => setIsZoomed(false)}>
            <X className="w-6 h-6" />
          </button>
          <img src={avatarUrl || ''} alt="Avatar" className="max-w-full max-h-[75vh] object-contain rounded-lg shadow-2xl" onClick={e => e.stopPropagation()} />
          <label htmlFor="avatar-upload-modal" className="mt-6 cursor-pointer border border-[color-mix(in_srgb,var(--gold)_60%,transparent)] hover:border-maslow-gold text-maslow-cream px-6 py-2.5 text-xs uppercase tracking-[0.2em] flex items-center gap-2 transition-colors" onClick={e => e.stopPropagation()}>
            <RotateCw className="w-3.5 h-3.5" /> Replace Photo
          </label>
          <input type="file" id="avatar-upload-modal" accept="image/*" onChange={e => { uploadAvatar(e); setIsZoomed(false); }} className="hidden" disabled={saving} />
        </div>
      )}
    </div>
  );
};

// ── Sub-components ──────────────────────────────────────────────────────────

const SectionLabel: React.FC<{ children: React.ReactNode; sub?: string }> = ({ children, sub }) => (
  <div>
    <h2 className="text-[13px] font-bold uppercase tracking-[0.24em] text-[color-mix(in_srgb,var(--gold)_75%,var(--navy))]">{children}</h2>
    {sub && <p className="text-sm text-[color-mix(in_srgb,var(--navy)_65%,transparent)] mt-1.5">{sub}</p>}
  </div>
);

const Field: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
  <div>
    <label className="block text-xs text-[color-mix(in_srgb,var(--navy)_62%,transparent)] uppercase tracking-[0.16em] mb-1">{label}</label>
    {children}
  </div>
);

const LineInput: React.FC<{ value: string; onChange: (v: string) => void; placeholder?: string; type?: string }> = ({ value, onChange, placeholder, type = 'text' }) => (
  <input
    type={type}
    value={value}
    onChange={e => onChange(e.target.value)}
    placeholder={placeholder}
    className="w-full bg-transparent text-base text-maslow-navy border-0 border-b border-[color-mix(in_srgb,var(--navy)_25%,transparent)] focus:border-maslow-gold rounded-none px-0 py-2 outline-none placeholder-[color-mix(in_srgb,var(--navy)_35%,transparent)] transition-colors"
  />
);

const ChoiceRow: React.FC<{ label: string; description?: string; options: { id: string; label: string }[]; value: string; onChange: (v: string) => void }> = ({ label, description, options, value, onChange }) => (
  <div className="flex flex-wrap items-start justify-between gap-x-6 gap-y-1 py-3 border-b border-[color-mix(in_srgb,var(--navy)_7%,transparent)]">
    <span className="min-w-0">
      <span className="block text-[17px] text-maslow-navy" style={{ fontFamily: 'var(--serif)' }}>{label}</span>
      {description && <span className="block text-sm text-[color-mix(in_srgb,var(--navy)_60%,transparent)] mt-0.5 leading-snug">{description}</span>}
    </span>
    <div className="flex gap-5 flex-shrink-0">
      {options.map(o => (
        <button
          key={o.id}
          onClick={() => onChange(o.id)}
          aria-pressed={value === o.id}
          className={`relative py-1 text-xs uppercase tracking-[0.14em] transition-opacity duration-200 ${value === o.id ? 'text-maslow-navy opacity-100' : 'text-maslow-navy opacity-45 hover:opacity-80'}`}
        >
          {o.label}
          <span className={`absolute left-0 right-0 bottom-0 h-px bg-maslow-gold origin-left transition-transform duration-200 ${value === o.id ? 'scale-x-100' : 'scale-x-0'}`} />
        </button>
      ))}
    </div>
  </div>
);

const KitRow: React.FC<{
  option: UsageOption;
  checked: boolean;
  items: string[];
  onToggle: () => void;
  onToggleItem: (itemId: string) => void;
}> = ({ option, checked, items, onToggle, onToggleItem }) => (
  <div className="border-b border-[color-mix(in_srgb,var(--navy)_7%,transparent)] last:border-b-0">
    <button onClick={onToggle} aria-pressed={checked} className="group w-full flex items-start justify-between gap-6 py-3 text-left">
      <span className="min-w-0">
        <span className="block text-[17px] text-maslow-navy" style={{ fontFamily: 'var(--serif)' }}>{option.label}</span>
        <span className="block text-sm text-[color-mix(in_srgb,var(--navy)_60%,transparent)] mt-0.5 leading-snug">{option.description}</span>
      </span>
      <span className={`mt-1 w-4 h-4 rounded-full border flex items-center justify-center flex-shrink-0 transition-all ${
        checked
          ? 'border-maslow-gold bg-maslow-gold'
          : 'border-[color-mix(in_srgb,var(--navy)_25%,transparent)] group-hover:border-[color-mix(in_srgb,var(--gold)_60%,transparent)]'
      }`}>
        {checked && <Check className="w-2.5 h-2.5 text-maslow-navy" strokeWidth={3} />}
      </span>
    </button>
    {checked && (
      <div className="pb-3 flex flex-wrap gap-x-5 gap-y-1">
        {option.items.map(it => {
          const on = items.includes(it.id);
          return (
            <button
              key={it.id}
              onClick={() => onToggleItem(it.id)}
              aria-pressed={on}
              className={`relative py-1 text-xs uppercase tracking-[0.14em] transition-opacity duration-200 ${on ? 'text-maslow-navy opacity-100' : 'text-maslow-navy opacity-45 hover:opacity-80'}`}
            >
              {it.label}
              <span className={`absolute left-0 right-0 bottom-0 h-px bg-maslow-gold origin-left transition-transform duration-200 ${on ? 'scale-x-100' : 'scale-x-0'}`} />
            </button>
          );
        })}
      </div>
    )}
  </div>
);

const PickRow: React.FC<{ label: string; description?: string; checked: boolean; onChange: () => void }> = ({ label, description, checked, onChange }) => (
  <button
    onClick={onChange}
    aria-pressed={checked}
    className="group w-full flex items-start justify-between gap-6 py-3 border-b border-[color-mix(in_srgb,var(--navy)_7%,transparent)] last:border-b-0 text-left transition-colors"
  >
    <span className="min-w-0">
      <span className="block text-[17px] text-maslow-navy transition-colors" style={{ fontFamily: 'var(--serif)' }}>
        {label}
      </span>
      {description && (
        <span className="block text-sm text-[color-mix(in_srgb,var(--navy)_60%,transparent)] mt-0.5 leading-snug">{description}</span>
      )}
    </span>
    <span className={`mt-1 w-4 h-4 rounded-full border flex items-center justify-center flex-shrink-0 transition-all ${
      checked
        ? 'border-maslow-gold bg-maslow-gold'
        : 'border-[color-mix(in_srgb,var(--navy)_25%,transparent)] group-hover:border-[color-mix(in_srgb,var(--gold)_60%,transparent)]'
    }`}>
      {checked && <Check className="w-2.5 h-2.5 text-maslow-navy" strokeWidth={3} />}
    </span>
  </button>
);

export default ProfilePage;
